"use client";

import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import type { iBrand } from "@/@sdk/brands/brand-model";
import { resolveBrandIdentity } from "@/@sdk/brands/resolve-brand-identity";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

export const brandDetailsSchema = z.object({
  legalName: z.string().optional(),
  atolNumber: z.string().optional(),
  termsAndConditionsUrl: z
    .string()
    .url("Must be a valid URL")
    .optional()
    .or(z.literal("")),
  bookingConditionsUrl: z
    .string()
    .url("Must be a valid URL")
    .optional()
    .or(z.literal("")),
  postalAddress: z.object({
    line1: z.string().optional(),
    line2: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    postalCode: z.string().optional(),
  }),
});

export type BrandDetailsFormValues = z.infer<typeof brandDetailsSchema>;

export const emptyBrandDetailsValues: BrandDetailsFormValues = {
  legalName: "",
  atolNumber: "",
  termsAndConditionsUrl: "",
  bookingConditionsUrl: "",
  postalAddress: {
    line1: "",
    line2: "",
    city: "",
    state: "",
    postalCode: "",
  },
};

export function brandToDetailsValues(
  brand: iBrand | undefined
): BrandDetailsFormValues {
  if (!brand) return emptyBrandDetailsValues;

  const identity = resolveBrandIdentity(brand);
  const legalNameFromIdentity =
    brand.legalName?.trim() ||
    (identity.legalName !== identity.displayName ? identity.legalName : "");

  return {
    legalName: legalNameFromIdentity,
    atolNumber: brand.atolNumber?.trim() || identity.atolNumber || "",
    termsAndConditionsUrl:
      brand.termsAndConditionsUrl?.trim() || identity.termsAndConditionsUrl || "",
    bookingConditionsUrl:
      brand.bookingConditionsUrl?.trim() || identity.bookingConditionsUrl || "",
    postalAddress: {
      line1: brand.postalAddress?.line1?.trim() || identity.postalAddress.line1 || "",
      line2: brand.postalAddress?.line2?.trim() || identity.postalAddress.line2 || "",
      city: brand.postalAddress?.city?.trim() || identity.postalAddress.city || "",
      state: brand.postalAddress?.state?.trim() || identity.postalAddress.state || "",
      postalCode:
        brand.postalAddress?.postalCode?.trim() ||
        identity.postalAddress.postalCode ||
        "",
    },
  };
}

type BrandDetailsFormProps = {
  defaultValues: BrandDetailsFormValues;
  formId?: string;
  onSubmit: (values: BrandDetailsFormValues) => Promise<void>;
};

export function BrandDetailsForm({
  defaultValues,
  formId = "brand-details-form",
  onSubmit,
}: BrandDetailsFormProps) {
  const form = useForm<BrandDetailsFormValues>({
    resolver: zodResolver(brandDetailsSchema),
    defaultValues,
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  return (
    <Form {...form}>
      <form
        id={formId}
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="legalName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Legal name</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Registered company name" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="atolNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>ATOL number</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="e.g. 12345" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="termsAndConditionsUrl"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Terms &amp; conditions URL</FormLabel>
              <FormControl>
                <Input {...field} placeholder="https://..." />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="bookingConditionsUrl"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Booking conditions URL</FormLabel>
              <FormControl>
                <Input {...field} placeholder="https://..." />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-3 rounded-lg border p-4">
          <p className="text-sm font-medium">Postal address</p>
          <FormField
            control={form.control}
            name="postalAddress.line1"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Address line 1</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="postalAddress.line2"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Address line 2</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="postalAddress.city"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>City</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="postalAddress.postalCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Postcode</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      </form>
    </Form>
  );
}
